import { Component, OnInit, OnDestroy, input, inject, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { DialogButtonConfig, IDialog } from './models/dialog.model';
import { AppDatepicker, AppTextarea, AppTextbox, ButtonComponent, ButtonConfig, CheckboxComponent, CheckboxConfig, CommonService, DatePickerComponent, DateRangePickerComponent, DateRangePickerConfig, DropDownConfig, DropdownComponent, DynamicForm, DynamicFormControl, RadioButtonComponent, RadioButtonConfig, SlideToggleComponent, TextareaComponent, TextboxComponent, TranslationService, slideToggle } from '@waseel-asp/tpa-waseel-lib-staging';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { getButtonConfig } from '../../../shared/functions/config-function';
import { ButtonColor, SYSTEM_CONST } from '../../../shared/constants/system-constants.model';
import { DialogService } from '../../services/dialog/dialog.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MimeTypeName } from '../../constants/mime-types';
import { HelperService } from '../../services/common/helper.service';
import { CommonAttachmentComponent } from '../common-attachment/common-attachment.component';
import { ToastrHelperService } from '../../services/common/toast-helper.service';

@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TextareaComponent,
    CheckboxComponent,
    TextboxComponent,
    DropdownComponent,
    DatePickerComponent,
    RadioButtonComponent,
    ButtonComponent,
    DateRangePickerComponent,
    SlideToggleComponent,
    MatDialogContent,
    MatDialogActions,
    TranslateModule,
    MatTooltipModule,
    CommonAttachmentComponent
  ],
  templateUrl: './dialog.component.html',
  styleUrl: './dialog.component.scss'
})
export class DialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<DialogComponent>>(MatDialogRef);
  dialogService = inject(DialogService);
  private translation = inject(TranslationService);
  private commonService = inject(CommonService);
  private helperService = inject(HelperService);
  private toastrHelperService = inject(ToastrHelperService);
  data = inject(MAT_DIALOG_DATA);

  elementRef = inject(ElementRef);
  @ViewChild('commonAttachment', { static: false }) commonAttachment!: CommonAttachmentComponent;
  formControls: DynamicForm;
  form: FormGroup;
  title: string;
  dialogButton: DialogButtonConfig;
  buttonConfig: ButtonConfig[] = [];
  displayButtons: ButtonConfig[] = [];
  cssclass: string;
  acceptFileType: string;
  closeButton = getButtonConfig(() => this.closeDialog(), 'close', 'icon', 'primary', '', true, null, 'close', null, null, null, ['close-btn']);
  saveUsingSubject: boolean = false;
  formConfig = input<IDialog>();

  ngOnInit(): void {
    if (this.formConfig() && Object.keys(this.formConfig())?.length != 0) {
      this.formControls = this.formConfig().formControls;
      this.form = this.formConfig().form;
      this.title = this.formConfig().title;
      this.dialogButton = this.formConfig().dialogButton;
      this.cssclass = this.formConfig().cssclass ? this.formConfig().cssclass : 'form-row';
      this.saveUsingSubject = this.formConfig().useSubject;
      this.acceptFileType = this.formConfig().fileType || MimeTypeName.default;
    } else {
      this.formControls = this.data.formControls;
      this.form = this.data.form;
      this.title = this.data.title;
      this.dialogButton = this.data.dialogButton;
      this.cssclass = this.data.cssclass ? this.data.cssclass : 'form-row';
      this.saveUsingSubject = this.data.useSubject;
      this.acceptFileType = this.data.fileType || MimeTypeName.default;
    }
    this.buttonConfig = this.getCreateButtonConfig();
    this.showButtonsBasedOnConfig();
  }

  getCreateButtonConfig = (): ButtonConfig[] => {
    return [
      {
        id: 'cancel',
        buttonText: 'Cancel',
        variant: 'raised',
        color: SYSTEM_CONST.PRIMARY as ButtonColor,
        callback: () => this.closeDialog(),
        isPrimary: true,
        cssClasses: ['btn', 'btn-secondary', 'large', 'text-capitalize'],
      },
      {
        id: 'save',
        buttonText: 'Save',
        callback: async () => await this.save(),
        isPrimary: true,
        variant: 'raised',
        color: 'primary',
        cssClasses: ['btn', 'btn-primary', 'large', 'text-capitalize'],
      },
      {
        id: 'reset',
        buttonText: 'Reset',
        callback: () => this.reset(),
        isPrimary: true,
        variant: 'raised',
        color: SYSTEM_CONST.PRIMARY as ButtonColor,
        cssClasses: ['btn', 'btn-secondary', 'large', 'text-capitalize'],
      },
      {
        id: 'markAsClosed',
        buttonText: 'Mark As Closed',
        callback: async () => await this.markAsClosed(),
        isPrimary: true,
        variant: 'raised',
        color: SYSTEM_CONST.PRIMARY as ButtonColor,
      },
      {
        id: 'update',
        buttonText: 'Update',
        callback: async () => await this.update(),
        isPrimary: true,
        variant: 'raised',
        color: SYSTEM_CONST.PRIMARY as ButtonColor,
      }
    ];
  };

  showButtonsBasedOnConfig(): void {
    const cancelButtonConfig = this.buttonConfig.find(config => config.id === 'cancel');
    const saveButtonConfig = this.buttonConfig.find(config => config.id === 'save');
    const resetButtonConfig = this.buttonConfig.find(config => config.id === 'reset');
    const markAsClosedButtonConfig = this.buttonConfig.find(config => config.id === 'markAsClosed');
    const updateButtonConfig = this.buttonConfig.find(config => config.id === 'update');
    if (markAsClosedButtonConfig && this.dialogButton?.markAsClosedButtonConfig?.showButton) {
      markAsClosedButtonConfig.buttonText = this.dialogButton.markAsClosedButtonConfig?.title ? this.dialogButton.markAsClosedButtonConfig?.title : 'Mark As Closed';
      this.displayButtons.push(markAsClosedButtonConfig);
    }
    if (updateButtonConfig && this.dialogButton?.updateButtonConfig?.showButton) {
      updateButtonConfig.buttonText = this.dialogButton.updateButtonConfig?.title ? this.dialogButton.updateButtonConfig?.title : 'Update';
      this.displayButtons.push(updateButtonConfig);
    }
    if (cancelButtonConfig && this.dialogButton?.cancelButtonConfig?.showButton) {
      cancelButtonConfig.buttonText = this.dialogButton.cancelButtonConfig?.title ? this.dialogButton.cancelButtonConfig?.title : 'Cancel';
      this.displayButtons.push(cancelButtonConfig);
    }
    if (resetButtonConfig && this.dialogButton?.resetButtonConfig?.showButton) {
      resetButtonConfig.buttonText = this.dialogButton.resetButtonConfig?.title ? this.dialogButton.resetButtonConfig?.title : 'Reset';
      this.displayButtons.push(resetButtonConfig);
    }
    if (saveButtonConfig && this.dialogButton?.saveButtonConfig?.showButton) {
      saveButtonConfig.buttonText = this.dialogButton.saveButtonConfig?.title ? this.dialogButton.saveButtonConfig?.title : 'Save';
      this.displayButtons.push(saveButtonConfig);
    }
  }

  convertInput(control: DynamicFormControl) {
    return (control.control as AppTextbox);
  }

  convertDropdown(control: DynamicFormControl) {
    return (control.control as DropDownConfig);
  }

  convertDatePicker(control: DynamicFormControl) {
    return (control.control as AppDatepicker);
  }

  convertDateRangePicker(control: DynamicFormControl) {
    return (control.control as DateRangePickerConfig);
  }

  convertButton(control: DynamicFormControl) {
    return (control.control as ButtonConfig);
  }

  convertCheckbox(control: DynamicFormControl) {
    return (control.control as CheckboxConfig);
  }

  convertTextbox(control: DynamicFormControl) {
    return (control.control as AppTextarea);
  }

  convertRadioButton(control: DynamicFormControl) {
    return (control.control as RadioButtonConfig);
  }

  convertSlidetoggle(control: DynamicFormControl) {
    return (control.control as slideToggle);
  }

  closeDialog(): void {
    this.reset();
    this.helperService.closeDialog();
  }

  reset(): void {
    this.form.reset();
    if (this.commonAttachment) {
      this.commonAttachment.uploadedFiles = [];
    }
  }

  async markAsTouched() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    this.commonService.isManualChangeTriggered.next(true);
    await this.helperService.scrollToInvalidController(this.elementRef);
  }

  async save() {
    await this.markAsTouched();
    if (this.form.valid)
      this.saveUsingSubject ? this.dialogService.emitValue({ event: 'save', data: this.form.value }) : this.dialogRef.close({ event: 'save', data: this.form.value });
  }

  async markAsClosed() {
    await this.markAsTouched();
    if (this.form.valid)
      this.dialogRef.close({ event: 'markAsClosed', data: this.form.value });
  }

  async createOrder() {
    await this.markAsTouched();
    if (this.form.valid)
      this.dialogRef.close({ event: 'createOrder', data: this.form.value });
  }

  async update() {
    await this.markAsTouched();
    if (this.form.valid)
      this.dialogRef.close({ event: 'update', data: this.form.value });
  }

  onFileChange(event: Event, formControlName: string) {
    const file = (event.target as HTMLInputElement).files[0];
    if (!this.checkFileExtension(file)) {
      const key = Object.keys(MimeTypeName).find(key => MimeTypeName[key] === this.acceptFileType);
      this.toastrHelperService.showErrorMessage(this.translation.instant('COMMON.FILE_VALIDATION', { fileName: key }));
      return;
    }
    this.form.get(formControlName).patchValue(file);
    (event.target as HTMLInputElement).value = '';
  }

  checkFileExtension(file: File) {
    if (this.acceptFileType == MimeTypeName.default)
      return true;
    return file.type == this.acceptFileType;
  }

  getFormControlValue(formControlName: string) {
    return this.form.get(formControlName).value;
  }

  getFileNameAndExtension(fileName: string) {
    if (fileName) {
      const name = fileName.split('.');
      const extension = name.pop();
      return { name: name.join('.'), extension: `.${extension}` };
    }
    return { name: '', extension: '' };
  }

  getFormControlOfFile(formControlName: string): FormControl {
    return this.form.get(formControlName) as FormControl;
  }

  ngOnDestroy() {
    this.form?.reset();
  }
}
